import { API } from '../../../services'
import { APIResponse, Filters, Pagination } from '../../../services/types'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import { defineStore } from 'pinia'
import { setPagination } from '../../../services/utils/utils'
import { useToast } from 'vuestic-ui'
import { isSuccessStatus } from '../../../utils/http'
import type { InventoryItemCategory } from '../types'

const { init } = useToast()

export const useItemCategoryStore = defineStore('itemCategoryStore', {
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
    itemCategories: [] as InventoryItemCategory[],
    itemCategory: null as InventoryItemCategory | null,
  }),
  getters: {
    getItemCategories(state) {
      return state.itemCategories
    },
  },
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setItemCategories(itemCategories: InventoryItemCategory[]) {
      this.itemCategories = itemCategories
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    addItemCategory(itemCategory: any) {
      this.itemCategories.unshift(itemCategory)
    },
    editItemCategory(editedItemCategory: any) {
      const itemCategoryIndex = this.itemCategories.findIndex(
        (itemCategory) => itemCategory.uuid == editedItemCategory?.uuid,
      )
      if (itemCategoryIndex !== -1) {
        this.itemCategories[itemCategoryIndex] = editedItemCategory
      }
    },
    removeItemCategory(itemCategoryUuid: string) {
      const foundItemCategoryIndex = this.itemCategories.findIndex(
        (itemCategory) => itemCategory.uuid == itemCategoryUuid,
      )
      if (foundItemCategoryIndex === -1) return
      this.itemCategories.splice(foundItemCategoryIndex, 1)
    },
    async dispatchGetItemCategories(filters?: Filters): Promise<any> {
      this.initLoading()

      const { status, data } = await API.itemCategory
        .getItemCategories(filters, this.pagination)
        .finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.setItemCategories(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchGetItemCategoriesByItemGroupId(itemGroupId: number): Promise<any> {
      this.initLoading()

      const { status, data } = await API.itemCategory
        .getItemCategoriesByItemGroupId(itemGroupId.toString())
        .finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.setItemCategories(data.data ?? [])
      }

      return {
        success: isSuccessStatus(status),
        data: data?.data ?? [],
      }
    },
    async dispatchCreateItemCategory(input: InventoryItemCategory): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.itemCategory.createItemCategory(input).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.addItemCategory(data.data)
        init({ message: 'Item Category created successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchDeleteItemCategory(itemCategory: InventoryItemCategory): Promise<APIResponse<null>> {
      this.initLoading()

      const { status } = await API.itemCategory.deleteItemCategory(itemCategory.uuid).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.removeItemCategory(itemCategory.uuid)
        init({ message: 'Item Category Deleted successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchUpdateItemCategory(input: InventoryItemCategory): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.itemCategory.updateItemCategory(input).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.editItemCategory(data.data)
        init({ message: 'Item Category Updated successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
  },
})
